'use client';

import { AiToolkitVideoPlayer } from '@app/(darkHeader)/ai/_shared/_components/AiToolkitVideoPlayer/AiToolkitVideoPlayer';
import { useAiToolkitContext } from '@app/(darkHeader)/ai/_shared/_context/AiToolkitContext';
import { AI_TOOLKIT_CATEGORIES } from '@app/(darkHeader)/ai/_shared/_context/types';
import { isVerticalAspectRatio } from '@app/(darkHeader)/ai/_shared/_utils/videoMetadata';
import { type JSX } from 'react';

import { cn } from '@videoblocks/design-system';

import { DEFAULT_SELECTION_DURATION, FilmStripSlider, type VideoSelection } from '../../FilmStripSlider';
import { VideoEditPlayerControls } from '../VideoEditPlayer/VideoEditPlayerControls';
import { useVideoEditMetadata } from '../VideoEditPlayer/useVideoEditMetadata';
import { useVideoEditPlayback } from '../VideoEditPlayer/useVideoEditPlayback';
import { useVideoEditSelection } from '../VideoEditPlayer/useVideoEditSelection';
import { VideoEditSelectionMetadataError } from './VideoEditSelectionMetadataError';

/** Keeps tall footage from pushing the filmstrip off-panel.*/
const VERTICAL_VIDEO_MAX_HEIGHT = 500;

const EMPTY_SELECTION: VideoSelection = { startTime: 0, endTime: 0, duration: 0 };

export type VideoEditSelectionViewProps = {
    /**
     * Media for a clip deep-linked from StockBlocks, used only until the effect in the panel has
     * put it into context. It exists so the server-rendered HTML shows the real video instead of an
     * empty player on the first paint.
     */
    fallbackVideoUrl?: string;
    fallbackAspectRatio?: number;
};

export const VideoEditSelectionView = ({
    fallbackVideoUrl,
    fallbackAspectRatio,
}: VideoEditSelectionViewProps = {}): JSX.Element => {
    const { submitStates } = useAiToolkitContext();
    const submitState = submitStates[AI_TOOLKIT_CATEGORIES.VIDEO_EDIT];
    const originalVideoUrl = submitState.originalVideoUrl || fallbackVideoUrl || '';
    const aspectRatio = submitState.aspectRatio ?? fallbackAspectRatio;

    const restoredSelection: VideoSelection | undefined =
        submitState.startTime != null
            ? {
                  startTime: submitState.startTime,
                  endTime: submitState.startTime + (submitState.duration ?? DEFAULT_SELECTION_DURATION),
                  duration: submitState.duration ?? DEFAULT_SELECTION_DURATION,
              }
            : undefined;

    const { videoMetadata, error } = useVideoEditMetadata({ videoSrc: originalVideoUrl, aspectRatio });
    const isVertical = isVerticalAspectRatio(aspectRatio);

    const { selection, onSelectionChange } = useVideoEditSelection({
        videoSrc: originalVideoUrl,
        videoDuration: videoMetadata?.duration ?? 0,
        initialSelection: restoredSelection,
    });

    const { videoRef, isPlaying, currentTime, onPlayPause, onSeek, onTimeUpdate, onPlay, onEnded } =
        useVideoEditPlayback({
            selection: selection ?? EMPTY_SELECTION,
            isReady: Boolean(selection),
        });

    return (
        <div className="flex flex-col items-center w-full">
            <div className={cn('relative w-full', isVertical && 'flex justify-center')}>
                <AiToolkitVideoPlayer
                    state="playback"
                    ref={videoRef}
                    src={originalVideoUrl}
                    controls={false}
                    autoPlay={false}
                    loop={false}
                    onTimeUpdate={onTimeUpdate}
                    onPlay={onPlay}
                    onEnded={onEnded}
                    onClick={onPlayPause}
                    className={cn('rounded-none rounded-t-md cursor-pointer', isVertical && 'aspect-9/16 w-auto')}
                    style={isVertical ? { maxHeight: VERTICAL_VIDEO_MAX_HEIGHT } : undefined}
                />

                {videoMetadata && (
                    <VideoEditPlayerControls
                        isPlaying={isPlaying}
                        currentTime={currentTime}
                        videoDuration={videoMetadata.duration}
                        onPlayPause={onPlayPause}
                    />
                )}
            </div>

            {error ? (
                <VideoEditSelectionMetadataError message={error} />
            ) : (
                <FilmStripSlider
                    className="mb-0"
                    videoSrc={originalVideoUrl}
                    videoMetadata={videoMetadata}
                    selection={selection ?? EMPTY_SELECTION}
                    onSelectionChange={onSelectionChange}
                    isPlaying={isPlaying}
                    onPlayPause={onPlayPause}
                    onSeek={onSeek}
                />
            )}
        </div>
    );
};
